/*
==============================================================================
Código hecho por Rcrea
Archivo: assets/section-rcrea-guten-testimonials.js
Sección: Güten Brew | Testimonios Bento
Función:
- Masonry dinámico
- Reveal de cards
- Video Shopify y MP4 desktop/mobile
- Poster visible para evitar cards negras
- Autoplay controlado por visibilidad
- Play/pausa manual
- Compatibilidad Shopify Theme Editor
==============================================================================
*/
(() => {
  const SECTION_SELECTOR = "[data-gt-testimonials]";
  const GRID_SELECTOR = "[data-gt-grid]";
  const CARD_SELECTOR = "[data-gt-card]";
  const MEDIA_CARD_SELECTOR = "[data-gt-media-card]";
  const VIDEO_SELECTOR = "[data-gt-video]";
  const INIT_ATTR = "data-gt-initialized";
  const mobileQuery = window.matchMedia("(max-width: 749px)");
  const getSections = (root = document) => {
    const sections = [];
    if (root.matches?.(SECTION_SELECTOR)) sections.push(root);
    root.querySelectorAll?.(SECTION_SELECTOR).forEach((section) => sections.push(section));
    return sections;
  };
  const addCleanup = (section, callback) => {
    if (!Array.isArray(section._gtCleanups)) section._gtCleanups = [];
    section._gtCleanups.push(callback);
  };
  const layoutMasonry = (section) => {
    const grid = section.querySelector(GRID_SELECTOR);
    if (!grid) return;
    const style = window.getComputedStyle(grid);
    const rowHeight = Number.parseFloat(style.gridAutoRows || "10") || 10;
    const rowGap = Number.parseFloat(style.rowGap || "0") || 0;
    const cards = Array.from(grid.querySelectorAll(CARD_SELECTOR));
    cards.forEach((card) => card.style.removeProperty("grid-row-end"));
    window.requestAnimationFrame(() => {
      cards.forEach((card) => {
        const height = card.getBoundingClientRect().height;
        if (!height) return;
        const span = Math.max(1, Math.ceil((height + rowGap) / (rowHeight + rowGap)));
        card.style.gridRowEnd = `span ${span}`;
      });
    });
  };
  const initMasonry = (section) => {
    let resizeTimer = null;
    const requestLayout = () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => layoutMasonry(section), 60);
    };
    layoutMasonry(section);
    window.addEventListener("resize", requestLayout, { passive: true });
    section.querySelectorAll("img").forEach((image) => {
      if (!image.complete) image.addEventListener("load", requestLayout, { once: true });
    });
    section.querySelectorAll(VIDEO_SELECTOR).forEach((video) => {
      ["loadedmetadata", "loadeddata", "canplay"].forEach((eventName) => {
        video.addEventListener(eventName, requestLayout);
        addCleanup(section, () => video.removeEventListener(eventName, requestLayout));
      });
    });
    if ("ResizeObserver" in window) {
      const observer = new ResizeObserver(requestLayout);
      const grid = section.querySelector(GRID_SELECTOR);
      if (grid) observer.observe(grid);
      section.querySelectorAll(CARD_SELECTOR).forEach((card) => observer.observe(card));
      addCleanup(section, () => observer.disconnect());
    }
    addCleanup(section, () => {
      window.clearTimeout(resizeTimer);
      window.removeEventListener("resize", requestLayout);
    });
  };
  const initReveal = (section) => {
    const cards = Array.from(section.querySelectorAll(CARD_SELECTOR));
    if (!cards.length) return;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reducedMotion || !("IntersectionObserver" in window)) {
      cards.forEach((card) => card.classList.add("is-visible"));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("is-visible");
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.08, rootMargin: "0px 0px -5% 0px" });
    cards.forEach((card, index) => {
      card.style.transitionDelay = `${Math.min(index * 55, 330)}ms`;
      observer.observe(card);
    });
    addCleanup(section, () => observer.disconnect());
  };
  const getActiveMediaWrapper = (card) => {
    const hasMobileMedia = card.classList.contains("gt-card--has-mobile-media");
    if (mobileQuery.matches && hasMobileMedia) {
      return card.querySelector('[data-gt-media-wrapper="mobile"]');
    }
    return card.querySelector('[data-gt-media-wrapper="desktop"]');
  };
  const getActiveVideo = (card) => {
    return getActiveMediaWrapper(card)?.querySelector(VIDEO_SELECTOR) || null;
  };
  const prepareVideo = (video) => {
    if (!video) return;
    const autoplay = video.dataset.gtAutoplay === "true";
    const loop = video.dataset.gtLoop === "true";
    video.playsInline = true;
    video.loop = loop;
    video.setAttribute("playsinline", "");
    video.setAttribute("webkit-playsinline", "");
    if (loop) {
      video.setAttribute("loop", "");
    } else {
      video.removeAttribute("loop");
    }
    if (autoplay) {
      video.muted = true;
      video.defaultMuted = true;
      video.setAttribute("muted", "");
      video.setAttribute("autoplay", "");
    } else {
      video.removeAttribute("autoplay");
    }
  };
  const requestVideoLoad = (video) => {
    if (!video || video.readyState > 0 || video.dataset.gtLoadRequested === "true") return;
    video.dataset.gtLoadRequested = "true";
    try {
      video.load();
    } catch (error) {}
  };
  const playVideo = (video, { unmute = false } = {}) => {
    if (!video) return Promise.resolve(false);
    prepareVideo(video);
    if (unmute) {
      video.muted = false;
      video.defaultMuted = false;
      video.removeAttribute("muted");
    } else {
      video.muted = true;
      video.defaultMuted = true;
      video.setAttribute("muted", "");
    }
    requestVideoLoad(video);
    const executePlay = () => {
      const promise = video.play();
      if (promise && typeof promise.then === "function") {
        return promise
          .then(() => true)
          .catch(() => false);
      }
      return Promise.resolve(true);
    };
    if (video.readyState >= 2) return executePlay();
    return new Promise((resolve) => {
      let resolved = false;
      const finish = () => {
        if (resolved) return;
        resolved = true;
        cleanup();
        executePlay().then(resolve);
      };
      const fail = () => {
        if (resolved) return;
        resolved = true;
        cleanup();
        resolve(false);
      };
      const cleanup = () => {
        video.removeEventListener("loadeddata", finish);
        video.removeEventListener("canplay", finish);
        video.removeEventListener("error", fail);
      };
      video.addEventListener("loadeddata", finish, { once: true });
      video.addEventListener("canplay", finish, { once: true });
      video.addEventListener("error", fail, { once: true });
      window.setTimeout(fail, 5000);
    });
  };
  const pauseVideo = (video) => {
    if (!video) return;
    try {
      video.pause();
    } catch (error) {}
  };
  const updatePlayButton = (card) => {
    const button = card.querySelector("[data-gt-play]");
    if (!button) return;
    const video = getActiveVideo(card);
    const autoplay = video?.dataset.gtAutoplay === "true";
    button.hidden = !video || autoplay;
    if (!video || autoplay) {
      card.classList.remove("is-playing");
      return;
    }
    const playing = !video.paused && !video.ended;
    card.classList.toggle("is-playing", playing);
    button.setAttribute("aria-label", playing ? "Pausar video" : "Reproducir video");
  };
  const pauseInactiveVideos = (card) => {
    const activeVideo = getActiveVideo(card);
    card.querySelectorAll(VIDEO_SELECTOR).forEach((video) => {
      prepareVideo(video);
      if (video !== activeVideo) pauseVideo(video);
    });
    updatePlayButton(card);
  };
  const syncMediaCard = (card, shouldAutoplay = false) => {
    pauseInactiveVideos(card);
    const activeVideo = getActiveVideo(card);
    if (!activeVideo) return;
    prepareVideo(activeVideo);
    const autoplay = activeVideo.dataset.gtAutoplay === "true";
    if (!autoplay || !shouldAutoplay) {
      if (!autoplay) updatePlayButton(card);
      return;
    }
    playVideo(activeVideo).then((played) => {
      card.classList.toggle("is-playing", played);
      updatePlayButton(card);
    });
  };
  const syncAllMediaCards = (section) => {
    section.querySelectorAll(MEDIA_CARD_SELECTOR).forEach((card) => {
      const visible = card.dataset.gtInView === "true";
      syncMediaCard(card, visible);
    });
  };
  const initVideoEvents = (section) => {
    section.querySelectorAll(VIDEO_SELECTOR).forEach((video) => {
      prepareVideo(video);
      requestVideoLoad(video);
      const card = video.closest(MEDIA_CARD_SELECTOR);
      const onPlay = () => {
        card?.classList.add("is-playing");
        if (card) updatePlayButton(card);
      };
      const onPause = () => {
        card?.classList.remove("is-playing");
        if (card) updatePlayButton(card);
      };
      const retryAutoplay = () => {
        if (!card || video !== getActiveVideo(card)) return;
        if (video.dataset.gtAutoplay !== "true" || card.dataset.gtInView !== "true") return;
        playVideo(video).then(() => updatePlayButton(card));
      };
      video.addEventListener("play", onPlay);
      video.addEventListener("pause", onPause);
      video.addEventListener("ended", onPause);
      video.addEventListener("loadeddata", retryAutoplay);
      video.addEventListener("canplay", retryAutoplay);
      video.addEventListener("stalled", retryAutoplay);
      addCleanup(section, () => {
        video.removeEventListener("play", onPlay);
        video.removeEventListener("pause", onPause);
        video.removeEventListener("ended", onPause);
        video.removeEventListener("loadeddata", retryAutoplay);
        video.removeEventListener("canplay", retryAutoplay);
        video.removeEventListener("stalled", retryAutoplay);
      });
    });
  };
  const initVideoControls = (section) => {
    const onClick = (event) => {
      const playButton = event.target.closest("[data-gt-play]");
      if (!playButton || !section.contains(playButton)) return;
      event.preventDefault();
      event.stopPropagation();
      const card = playButton.closest(MEDIA_CARD_SELECTOR);
      const video = card ? getActiveVideo(card) : null;
      if (!card || !video) return;
      if (!video.paused && !video.ended) {
        pauseVideo(video);
        updatePlayButton(card);
        return;
      }
      playVideo(video, { unmute: true }).then(() => updatePlayButton(card));
    };
    section.addEventListener("click", onClick);
    const mediaChange = () => {
      section.querySelectorAll(MEDIA_CARD_SELECTOR).forEach((card) => {
        card.querySelectorAll(VIDEO_SELECTOR).forEach(pauseVideo);
        syncMediaCard(card, card.dataset.gtInView === "true");
      });
      window.setTimeout(() => layoutMasonry(section), 100);
    };
    if (typeof mobileQuery.addEventListener === "function") {
      mobileQuery.addEventListener("change", mediaChange);
      addCleanup(section, () => mobileQuery.removeEventListener("change", mediaChange));
    } else if (typeof mobileQuery.addListener === "function") {
      mobileQuery.addListener(mediaChange);
      addCleanup(section, () => mobileQuery.removeListener(mediaChange));
    }
    addCleanup(section, () => {
      section.removeEventListener("click", onClick);
      section.querySelectorAll(VIDEO_SELECTOR).forEach(pauseVideo);
    });
  };
  const initVisibilityVideoObserver = (section) => {
    const cards = Array.from(section.querySelectorAll(MEDIA_CARD_SELECTOR));
    if (!cards.length) return;
    if (!("IntersectionObserver" in window)) {
      cards.forEach((card) => {
        card.dataset.gtInView = "true";
        syncMediaCard(card, true);
      });
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const card = entry.target;
        card.dataset.gtInView = entry.isIntersecting ? "true" : "false";
        const video = getActiveVideo(card);
        if (!entry.isIntersecting) {
          pauseVideo(video);
          updatePlayButton(card);
          return;
        }
        syncMediaCard(card, true);
      });
    }, {
      threshold: 0.12,
      rootMargin: "120px 0px 120px 0px"
    });
    cards.forEach((card) => observer.observe(card));
    addCleanup(section, () => observer.disconnect());
  };
  const initSection = (section) => {
    if (!section || section.hasAttribute(INIT_ATTR)) return;
    section.setAttribute(INIT_ATTR, "true");
    section._gtCleanups = [];
    initMasonry(section);
    initReveal(section);
    initVideoEvents(section);
    initVideoControls(section);
    initVisibilityVideoObserver(section);
    section.querySelectorAll(MEDIA_CARD_SELECTOR).forEach(updatePlayButton);
    window.setTimeout(() => {
      layoutMasonry(section);
      syncAllMediaCards(section);
    }, 250);
    window.setTimeout(() => {
      layoutMasonry(section);
      syncAllMediaCards(section);
    }, 900);
  };
  const destroySection = (section) => {
    if (!section) return;
    if (Array.isArray(section._gtCleanups)) {
      section._gtCleanups.forEach((cleanup) => {
        if (typeof cleanup === "function") cleanup();
      });
    }
    section._gtCleanups = [];
    section.removeAttribute(INIT_ATTR);
  };
  const initAll = (root = document) => {
    getSections(root).forEach(initSection);
  };
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll());
  } else {
    initAll();
  }
  document.addEventListener("shopify:section:load", (event) => {
    initAll(event.target);
  });
  document.addEventListener("shopify:section:unload", (event) => {
    getSections(event.target).forEach(destroySection);
  });
  document.addEventListener("shopify:block:select", (event) => {
    const section = event.target.closest?.(SECTION_SELECTOR);
    if (!section) return;
    const card = event.target.closest?.(CARD_SELECTOR);
    if (!card) return;
    card.classList.add("is-visible");
    window.setTimeout(() => {
      layoutMasonry(section);
      syncMediaCard(card, true);
      card.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest"
      });
    }, 80);
  });
})();